import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode,
	type Context,
} from 'react';

import type { CreditsBannerState } from '../common/credits-ui-state/types';
import {
	createCreditsBackendClient,
	type CreditRequestResult,
	type CreditsProduct,
} from '../controllers/credits-backend-client';
import { CreditRequestSuccessFlag } from '../internal/credit-request-success-flag';

export type { CreditRequestResult } from '../controllers/credits-backend-client';

export type CreditsRequestState = {
	isPending: boolean;
	result: CreditRequestResult | null;
};
type RequestContext = CreditsRequestState & {
	start: () => void;
	isUnavailable: boolean;
};
const CreditsRequestContext: Context<RequestContext | null> = createContext<RequestContext | null>(
	null,
);
export const useCreditsRequestContext = (): RequestContext | null =>
	useContext(CreditsRequestContext);
/** Shared request progress and outcome; successful requests also show package-owned confirmation feedback. */
export const useCreditsRequest = (): CreditsRequestState => {
	const value = useCreditsRequestContext();
	return { isPending: value?.isPending ?? false, result: value?.result ?? null };
};
type Props = {
	children: ReactNode;
	product: CreditsProduct;
	cloudId?: string;
	agentId?: string;
	state: CreditsBannerState | null;
	enabled: boolean;
	onResult?: (result: CreditRequestResult) => void;
};
const RequestProvider = ({
	children,
	product,
	cloudId,
	agentId,
	state,
	enabled,
	onResult,
}: Props) => {
	const [successScope, setSuccessScope] = useState<string | null>(null);
	const [isPending, setPending] = useState(false);
	const [result, setResult] = useState<CreditRequestResult | null>(null);
	const inFlight = useRef(false);
	const controller = useRef<AbortController | null>(null);
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			controller.current?.abort();
		};
	}, []);
	const method =
		state?.balanceState === 'depleted' && state.scope === 'USER' ? state.requestMethod : undefined;
	const eligible = enabled && Boolean(cloudId?.trim()) && Boolean(method);
	// Only identity changes invalidate an in-flight POST.
	const scopeKey = JSON.stringify([product, cloudId, agentId, enabled]);
	const creditStateKey = JSON.stringify([
		state?.balanceState,
		state?.scope,
		state?.resetDate,
		method,
	]);
	const current = useRef({ eligible, method, scopeKey });
	current.current = { eligible, method, scopeKey };
	useEffect(() => {
		controller.current?.abort();
		controller.current = null;
		inFlight.current = false;
		setPending(false);
		setSuccessScope(null);
		setResult(null);
	}, [scopeKey]);
	useEffect(() => {
		// Polling may update metadata while a request is being delivered.
		// Keep that request and its outcome, but reset idle state for a new credit decision.
		if (!inFlight.current) {
			setResult(null);
		}
	}, [creditStateKey, eligible, method?.mode]);
	const send = async () => {
		if (inFlight.current || !current.current.eligible || current.current.method?.mode !== 'EMAIL') {
			return;
		}
		inFlight.current = true;
		setSuccessScope(null);
		setPending(true);
		setResult(null);
		const abortController = new AbortController();
		controller.current = abortController;
		const timeout = setTimeout(() => abortController.abort(), 30_000);
		let outcome: CreditRequestResult;
		try {
			outcome = await createCreditsBackendClient({ product, cloudId, agentId }).requestCredits({
				signal: abortController.signal,
			});
		} catch {
			outcome = { outcome: 'failed' };
		} finally {
			clearTimeout(timeout);
			if (controller.current === abortController) {
				inFlight.current = false;
			}
		}
		if (
			!mounted.current ||
			controller.current !== abortController ||
			current.current.scopeKey !== scopeKey
		) {
			return;
		}
		setPending(false);
		setResult(outcome);
		if (outcome.outcome === 'accepted') {
			setSuccessScope(scopeKey);
		}
		onResult?.(outcome);
	};
	const start = () => {
		if (!eligible || inFlight.current) {
			return;
		}
		if (method?.mode === 'REDIRECT_URL') {
			window.open(method.redirectUrl, '_blank', 'noopener,noreferrer');
		} else {
			void send();
		}
	};
	return (
		<CreditsRequestContext.Provider
			value={{
				isPending,
				result,
				start,
				isUnavailable: !eligible,
			}}
		>
			{children}
			{successScope === scopeKey && (
				<CreditRequestSuccessFlag onDismiss={() => setSuccessScope(null)} />
			)}
		</CreditsRequestContext.Provider>
	);
};
export const CreditsRequestProvider = (props: Props): React.JSX.Element => (
	<RequestProvider {...props} />
);
